import React, { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from 'lib/utils';
import { RenderedHtml } from 'modules/editor';

interface ReorderItem {
  id: string;
  content: string;
}

interface ReorderPlayerProps {
  items: ReorderItem[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

interface SortableItemProps {
  item: ReorderItem;
  disabled?: boolean;
}

function SortableItem({ item, disabled }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm transition-colors',
        isDragging && 'z-50 border-foreground shadow-lg',
        !disabled && 'cursor-grab hover:border-foreground/30',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <button
        type="button"
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded text-muted-foreground/70',
          !disabled && 'hover:bg-muted hover:text-foreground',
        )}
        {...attributes}
        {...listeners}
        disabled={disabled}
      >
        <GripVertical size={16} />
      </button>
      <RenderedHtml html={item.content} inline className="flex-1 text-sm text-foreground" />
    </div>
  );
}

/** Draggable list. Uses @dnd-kit for accessible drag and drop. */
export function ReorderPlayer({
  items,
  value,
  onChange,
  disabled = false,
}: ReorderPlayerProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Get ordered items based on current value (or use original order)
  const orderedItems = value.length > 0
    ? value.map((id) => items.find((item) => item.id === id)).filter(Boolean) as ReorderItem[]
    : items;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const currentOrder = orderedItems.map((item) => item.id);
        const oldIndex = currentOrder.indexOf(active.id as string);
        const newIndex = currentOrder.indexOf(over.id as string);

        const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
        onChange(newOrder);
      }
    },
    [orderedItems, onChange],
  );

  return (
    <div className="reorder-player">
      <p className="mb-3 text-xs text-muted-foreground">
        Drag and drop to arrange items in the correct order.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedItems.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-2">
            {orderedItems.map((item) => (
              <SortableItem key={item.id} item={item} disabled={disabled} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default ReorderPlayer;
